/**
 * Comments Section Component
 * Displays and manages comments on report sections
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import { MessageSquare, Send, Trash2, Edit2, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Comment {
  id: string;
  report_id: string;
  author_id: string;
  author_type: 'accountant' | 'user';
  author_name: string;
  section: string;
  comment_text: string;
  created_at: string;
  updated_at?: string;
  status: string;
}

interface CommentsSectionProps {
  reportId: string;
  section: string;
  sectionTitle: string;
  getAuthHeader: () => any;
  canComment: boolean;
}

export default function CommentsSection({
  reportId,
  section,
  sectionTitle,
  getAuthHeader,
  canComment
}: CommentsSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchComments();
  }, [reportId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/comments`,
        { headers: getAuthHeader() }
      );
      
      // Filter comments for this section
      const sectionComments = response.data.filter(
        (c: Comment) => c.section === section
      );
      setComments(sectionComments);
    } catch (err: any) {
      console.error('Failed to fetch comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/comments`,
        {
          report_id: reportId,
          section: section,
          comment_text: newComment.trim()
        },
        { headers: getAuthHeader() }
      );

      setComments([...comments, response.data]);
      setNewComment('');
    } catch (err: any) {
      console.error('Failed to submit comment:', err);
      setError(err.response?.data?.detail || 'Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditText(comment.comment_text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="border rounded-lg p-4 mt-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />
          <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Comments on {sectionTitle}
          </h3>
          {comments.length > 0 && (
            <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {comments.length}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="border px-4 py-2 rounded mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
          {error}
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-3 mb-4">
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="text-sm italic" style={{ color: 'var(--color-text-muted)' }}>
            No comments yet. Be the first to comment!
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="border-l-4 pl-4 py-2" style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {comment.author_name}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded" style={
                      comment.author_type === 'accountant'
                        ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                    }>
                      {comment.author_type === 'accountant' ? 'Accountant' : 'User'}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>

                  {editingId === comment.id ? (
                    <div className="mt-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm"
                        style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                        rows={3}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={handleCancelEdit}
                          className="flex items-center gap-1 px-3 py-1 text-sm"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
                      {comment.comment_text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* New Comment Form */}
      {canComment && (
        <form onSubmit={handleSubmitComment} className="border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
            rows={3}
            disabled={submitting}
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
